import { useGuestPayorOnboarding } from '@melio/ar-domain';
import { useToast } from '@melio/penny';
import { PlaidAccountData, useFundingSources } from '@melio/platform-api';
import { forwardRef, useBoolean } from '@melio/platform-utils';
import React from 'react';

import { AddBankFundingSourceFormScreen, AddPlaidFundingSourceScreen } from './screens';

type AddBankFundingSourceActivityProps = {
  onDone: (id: string) => void;
  paymentRequestLink: string;
};

export const AddBankFundingSourceActivity = forwardRef<AddBankFundingSourceActivityProps>(
  ({ paymentRequestLink, onDone }, ref) => {
    const { onboarding, isLoading: isLoadingOnBoarding } = useGuestPayorOnboarding();
    const { create } = useFundingSources({ enabled: false });
    const [isPlaidModalOpen, openPlaidModal] = useBoolean(false);
    const [isLoadingPlaidToken, loadingPlaidToken] = useBoolean(false);
    const toast = useToast();

    const createPlaidFundingSource = (data: PlaidAccountData) => {
      openPlaidModal.off();
      void create({
        type: 'plaid',
        details: { plaidAccountId: data.accounts[0]?.id as string, plaidToken: data.public_token },
      }).then(({ id }) => onDone(id));
    };

    const plaidErrorHandler = (error: PlatformError) => {
      openPlaidModal.off();
      toast({ type: 'error', title: error.message });
    };

    const handleOnSubmit = async ({ email }: { email: string }) => {
      await onboarding({ email, paymentRequestLink });
      openPlaidModal.on();
    };

    return (
      <>
        <AddBankFundingSourceFormScreen
          onSubmit={handleOnSubmit}
          isSaving={isLoadingPlaidToken || isLoadingOnBoarding}
          ref={ref}
        />
        {isPlaidModalOpen ? (
          <AddPlaidFundingSourceScreen
            onLoadToken={(isLoading) => {
              if (isLoading) {
                loadingPlaidToken.on();
              } else {
                loadingPlaidToken.off();
              }
            }}
            onSuccess={createPlaidFundingSource}
            onClose={() => openPlaidModal.off()}
            onError={plaidErrorHandler}
          />
        ) : null}
      </>
    );
  }
);
AddBankFundingSourceActivity.displayName = 'AddCardFundingSourceActivity';
